import { err, ok, type Result } from '../result.ts';

// Clinical notes (docs/plans/phase1-consultation.md §2.2, §5.1). A note is a list of
// sections in template order. Drafts are saved in place until signed; a signed note is
// never edited, only amended with a reason (a new version) or entered in error.

/** Characters per section; a note is long free text, not a document store. */
export const NOTE_SECTION_MAX_CHARS = 20_000;
export const NOTE_MAX_SECTIONS = 20;

export interface NoteSection {
  key: string;
  heading: string;
  text: string;
}

export interface TemplateSection {
  key: string;
  heading: string;
  hint: string | null;
  defaultText: string | null;
}

export type NoteSectionsErrorCode =
  | 'note.no_sections'
  | 'note.too_many_sections'
  | 'note.duplicate_key'
  | 'note.bad_key'
  | 'note.heading_blank'
  | 'note.section_too_long';

export const NOTE_SECTION_ERRORS: Record<NoteSectionsErrorCode, string> = {
  'note.no_sections': 'The note has no sections',
  'note.too_many_sections': `A note can have at most ${String(NOTE_MAX_SECTIONS)} sections`,
  'note.duplicate_key': 'Each section of the note can appear once',
  'note.bad_key': 'A section key is not valid',
  'note.heading_blank': 'Every section needs a heading',
  'note.section_too_long': `A section can be at most ${NOTE_SECTION_MAX_CHARS.toLocaleString('en-KE')} characters`,
};

const KEY = /^[a-z][a-z0-9_]{0,39}$/;

/** Checks a note's sections and normalises line endings to `\n`. */
export function validateNoteSections(
  sections: readonly NoteSection[],
): Result<NoteSection[], NoteSectionsErrorCode> {
  if (sections.length === 0) return err('note.no_sections');
  if (sections.length > NOTE_MAX_SECTIONS) return err('note.too_many_sections');
  const seen = new Set<string>();
  const out: NoteSection[] = [];
  for (const s of sections) {
    if (!KEY.test(s.key)) return err('note.bad_key');
    if (seen.has(s.key)) return err('note.duplicate_key');
    seen.add(s.key);
    if (s.heading.trim() === '') return err('note.heading_blank');
    const text = s.text.replace(/\r\n?/g, '\n');
    if (text.length > NOTE_SECTION_MAX_CHARS) return err('note.section_too_long');
    out.push({ key: s.key, heading: s.heading.trim(), text });
  }
  return ok(out);
}

/** Nothing written in any section. An empty note can't be signed. */
export function isNoteEmpty(sections: readonly NoteSection[]): boolean {
  return sections.every((s) => s.text.trim() === '');
}

/**
 * The note as canonical JSON: sections in their order, each with its keys in a fixed
 * order. Its SHA-256 identifies a version on print and export.
 */
export function canonicalNoteJson(sections: readonly NoteSection[]): string {
  return JSON.stringify(sections.map((s) => ({ key: s.key, heading: s.heading, text: s.text })));
}

/** A new note's sections from its template: default text where the template has one. */
export function sectionsFromTemplate(sections: readonly TemplateSection[]): NoteSection[] {
  return sections.map((s) => ({ key: s.key, heading: s.heading, text: s.defaultText ?? '' }));
}

export const NOTE_STATUSES = ['draft', 'signed', 'entered_in_error'] as const;
export type NoteStatus = (typeof NOTE_STATUSES)[number];

export const NOTE_ACTIONS = ['save_draft', 'sign', 'amend', 'enter_in_error'] as const;
export type NoteAction = (typeof NOTE_ACTIONS)[number];

/**
 * What an action does to a note. Drafts are saved and signed; signed notes are amended
 * (still signed, a new version) or entered in error, which is final.
 */
export function noteTransition(
  from: NoteStatus,
  action: NoteAction,
): Result<NoteStatus, 'note.not_allowed'> {
  if (from === 'draft' && action === 'save_draft') return ok('draft');
  if (from === 'draft' && action === 'sign') return ok('signed');
  if (from === 'signed' && action === 'amend') return ok('signed');
  if (from === 'signed' && action === 'enter_in_error') return ok('entered_in_error');
  return err('note.not_allowed');
}
